import React from "react";
import { useTranslation } from "react-i18next";
import { svgs } from "../../../../../../core/constants/svgs";
import { PLACEHOLDER_IMAGE_SMALL_URL, PLACEHOLDER_IMAGE_MEDIUM_URL } from "../../../../../../core/constants/constants";
import useIsMobile from "../../../../../../core/hooks/useMobile";

const CoverPhotoUpload = ({
  coverPhotoInputId,
  coverPhotoImageId,
  onUploadImageClick,
  onUploadImageChange,
  onRemoveImage,
  onGenerateWithAI,
  onImageClick,
  compact = false,
  bookTitle = "",
  hasImage = false,
  isGenerating = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [showModal, setShowModal] = React.useState(false);

  const handleGenerateClick = async () => {
    setShowModal(false);
    await onGenerateWithAI();
  };

  const handleUploadClick = () => {
    setShowModal(false);
    onUploadImageClick(coverPhotoInputId)();
  };

  const handleImagePreviewClick = () => {
    if (hasImage && onImageClick) {
      onImageClick();
    }
  };

  if (compact) {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <input
          type="file"
          multiple={false}
          accept="image/jpeg, image/png, image/jpg"
          id={coverPhotoInputId}
          className="d-none"
          onChange={onUploadImageChange("cover", "photo")}
        />

        {/* Upload trigger area */}
        <div
          id={"upload-area-" + coverPhotoInputId}
          className="cursor-pointer"
          onClick={() => !hasImage && !isGenerating && setShowModal(true)}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
            position: "relative",
          }}
        >
          {/* Icon */}
          {!isGenerating && (
            <div
              style={{
                width: "28px",
                height: "28px",
                backgroundColor: "#f0d4c3",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#d4a574",
                  borderRadius: "50%",
                  position: "absolute",
                  top: "5px",
                  left: "7px",
                }}
              />
              <div
                style={{
                  width: "0",
                  height: "0",
                  borderLeft: "10px solid transparent",
                  borderRight: "0px solid transparent",
                  borderBottom: "10px solid #d4a574",
                  position: "absolute",
                  bottom: "5px",
                  left: "5px",
                }}
              />
            </div>
          )}

          {/* Loader for AI generation */}
          {isGenerating && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "3px solid #f3f3f3",
                  borderTop: "3px solid #e8935f",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          )}
        </div>

        {/* Image preview container */}
        <div
          id={"container-" + coverPhotoInputId}
          className="d-none"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "transparent",
          }}
        >
          <div
            className="img-delete-button"
            onClick={onRemoveImage(coverPhotoInputId, coverPhotoImageId)}
            style={{
              position: "absolute",
              top: "-8px",
              right: "-8px",
              width: "20px",
              height: "20px",
              backgroundColor: "#dc3545",
              color: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              cursor: "pointer",
              zIndex: 10,
              border: "2px solid white",
              fontWeight: "bold",
            }}
          >
            ×
          </div>
          <img
            id={coverPhotoImageId}
            className="img-fluid"
            src={PLACEHOLDER_IMAGE_SMALL_URL}
            onClick={handleImagePreviewClick}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "8px",
              cursor: hasImage ? "pointer" : "default",
            }}
            alt=""
          />
        </div>

        {/* Modal */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={() => setShowModal(false)}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "20px",
                width: "90%",
                maxWidth: "300px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h5 style={{ marginBottom: "20px", textAlign: "center" }}>
                {t("choose_cover_option")}
              </h5>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={handleUploadClick}
                  style={{
                    flex: 1,
                    padding: "12px",
                    backgroundColor: "#e8935f",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  {t("upload_image")}
                </button>
                <button
                  onClick={handleGenerateClick}
                  disabled={isGenerating}
                  style={{
                    flex: 1,
                    padding: "12px",
                    backgroundColor: isGenerating ? "#ccc" : "#6c63ff",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: isGenerating ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  {isGenerating ? t("generating") : t("ai_generate")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="cover-photo-book-container"
      style={{
        width: isMobile ? "280px" : "320px",
        margin: "0 auto",
        marginBottom: "2rem",
      }}
    >
      <input
        type="file"
        multiple={false}
        accept="image/jpeg, image/png, image/jpg"
        id={coverPhotoInputId}
        className="d-none"
        onChange={onUploadImageChange("cover", "photo")}
      />

      {/* Book-like container */}
      <div
        className="book-cover-wrapper"
        style={{
          position: "relative",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          padding: "40px 30px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
        }}
      >
        {/* Book spine */}
        <div
          className="book-spine"
          style={{
            position: "absolute",
            left: 0,
            top: "10px",
            bottom: "10px",
            width: "12px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px 0 0 8px",
            boxShadow: "inset -2px 0 4px rgba(0, 0, 0, 0.1)",
          }}
        />

        {/* Upload area or image preview */}
        <div
          id={"upload-area-" + coverPhotoInputId}
          className="cursor-pointer"
          onClick={() => !hasImage && !isGenerating && setShowModal(true)}
          style={{
            width: "140px",
            height: "140px",
            border: "3px dashed #e8935f",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            backgroundColor: "#fef9f5",
            transition: "all 0.3s ease",
            position: "relative",
            cursor: hasImage || isGenerating ? "default" : "pointer",
          }}
          onMouseOver={(e) => {
            if (!hasImage && !isGenerating) {
              e.currentTarget.style.backgroundColor = "#fef5ed";
              e.currentTarget.style.transform = "scale(1.02)";
            }
          }}
          onMouseOut={(e) => {
            if (!hasImage && !isGenerating) {
              e.currentTarget.style.backgroundColor = "#fef9f5";
              e.currentTarget.style.transform = "scale(1)";
            }
          }}
        >
          {/* Icon */}
          {!hasImage && !isGenerating && (
            <div
              style={{
                width: "50px",
                height: "50px",
                backgroundColor: "#f0d4c3",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: "#d4a574",
                  borderRadius: "50%",
                  position: "absolute",
                  top: "8px",
                  left: "12px",
                }}
              />
              <div
                style={{
                  width: "0",
                  height: "0",
                  borderLeft: "20px solid transparent",
                  borderRight: "0px solid transparent",
                  borderBottom: "20px solid #d4a574",
                  position: "absolute",
                  bottom: "8px",
                  left: "8px",
                }}
              />
            </div>
          )}

          {/* Loading spinner for AI generation */}
          {isGenerating && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #e8935f",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#e8935f",
                  fontWeight: "500",
                  margin: 0,
                  textAlign: "center",
                }}
              >
                {t("generating")}...
              </p>
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          )}
        </div>

        {/* Hidden image preview container */}
        <div
          id={"container-" + coverPhotoInputId}
          className="d-none"
          style={{
            position: "absolute",
            top: "40px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "140px",
            height: "140px",
          }}
        >
          <div
            className="img-delete-button"
            onClick={onRemoveImage(coverPhotoInputId, coverPhotoImageId)}
            style={{
              position: "absolute",
              top: "-8px",
              right: "-8px",
              width: "24px",
              height: "24px",
              backgroundColor: "#dc3545",
              color: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              cursor: "pointer",
              zIndex: 10,
              border: "2px solid white",
              fontWeight: "bold",
            }}
          >
            ×
          </div>
          <img
            id={coverPhotoImageId}
            className="img-fluid"
            src={PLACEHOLDER_IMAGE_MEDIUM_URL}
            onClick={handleImagePreviewClick}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "12px",
              cursor: hasImage ? "pointer" : "default",
            }}
            alt=""
          />
        </div>

        {/* Upload text */}
        {!hasImage && !isGenerating && (
          <div
            style={{
              textAlign: "center",
            }}
          >
            <p
              style={{
                color: "#e8935f",
                fontSize: "14px",
                fontWeight: "600",
                margin: "0 0 8px 0",
              }}
            >
              {/* {t("upload_cover_photo")} */}
              <br />
              <span style={{ fontSize: "12px" }}>({t("optional")})</span>
            </p>
          </div>
        )}

        {/* Book title at bottom */}
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "80%",
            textAlign: "center",
          }}
        >
          <p
            style={{
              color: "#333",
              fontSize: "13px",
              fontWeight: "500",
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {bookTitle || t("autobiography")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoverPhotoUpload;
